import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShieldPlus, Pencil } from "lucide-react";
import { QualificationsDialog } from "@/components/QualificationsDialog";
import { VolunteerContactDialog } from "@/components/VolunteerContactDialog";
import { SanitariList } from "@/components/SanitariList";

export default function VolunteersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [contact, setContact] = useState(null);
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const isCoord = user.role === "coordinatore";

  useEffect(() => {
    if (!isCoord) { navigate("/"); return; }
    api.get("/volunteers").then(({ data }) => setVolunteers(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossibile caricare i volontari"))
      .finally(() => setLoading(false));
  }, [isCoord, navigate]);

  if (!isCoord) return null;
  const onSaved = (u) => setVolunteers((p) => p.map((v) => (v.id === u.id ? { ...v, ...u } : v)));
  const missing = (v) => !v.phone || !v.email;
  const shown = volunteers.filter((v) => v.name.toLowerCase().includes(q.toLowerCase()) && (!onlyMissing || missing(v)));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="volunteers-back" onClick={() => navigate("/")} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <p className="font-display font-bold">Volontari e qualifiche</p>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-lg font-semibold mb-3">Volontari ({volunteers.length})</h3>
          <Input data-testid="volunteers-search" placeholder="Cerca per nome…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" />
          <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer w-fit">
            <input type="checkbox" data-testid="only-missing" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="accent-[#D32F2F]" />
            Solo con dati mancanti ({volunteers.filter(missing).length})
          </label>
          <div className="space-y-2 max-h-[560px] overflow-y-auto" data-testid="coord-volunteers-list">
            {loading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
            {shown.map((v) => (
              <div key={v.id} data-testid={`coord-volunteer-${v.id}`} className="rounded-lg border border-border p-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{v.name}</p>
                  {v.email && <p className="text-xs text-muted-foreground truncate">{v.email}</p>}
                  {v.phone && <a href={`tel:${v.phone}`} className="text-xs text-[#D32F2F] hover:underline">{v.phone}</a>}
                  {missing(v) && <p className="text-[11px] text-amber-700" data-testid={`missing-data-${v.id}`}>Mancano: {[!v.email && "email", !v.phone && "telefono"].filter(Boolean).join(", ")}</p>}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {v.qualifications.length === 0 && <span className="text-xs text-muted-foreground">Nessuna qualifica</span>}
                    {v.qualifications.map((x) => <Badge key={x} variant="secondary" className="text-[10px]">{x}</Badge>)}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {user.is_admin && (
                    <Button size="sm" variant="outline" data-testid={`edit-contact-${v.id}`} onClick={() => setContact(v)} className="rounded-full text-xs h-7">
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Anagrafica
                    </Button>
                  )}
                  <Button size="sm" variant="outline" data-testid={`edit-quals-${v.id}`} onClick={() => setEditing(v)} className="rounded-full text-xs h-7">
                    <ShieldPlus className="h-3.5 w-3.5 mr-1" /> Qualifiche
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <SanitariList volunteers={volunteers} setVolunteers={setVolunteers} />
      </main>
      <QualificationsDialog volunteer={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={onSaved} />
      <VolunteerContactDialog volunteer={contact} onOpenChange={(o) => !o && setContact(null)} onSaved={onSaved} />
    </div>
  );
}
